// Helpers for rendering a user's display name while accounting for
// soft-deletion. The backend exposes two different signals depending on the
// payload, so detection has to handle both:
//   - `is_deleted` flag on payloads that expose it (e.g. conversation view).
//   - a scrubbed name on payloads that don't: the user's `name` (or, where the
//     name is split, `first_name`) is replaced with the literal "Deleted" and
//     `last_name` is null.
import { combineName } from './textTransform';

const DELETED_NAME = 'Deleted';

// Note: conversations use the narrower conversationLock.isDeletedUser, which
// keys on the `is_deleted` flag only (the conversation payload always carries
// it). Import that one for conversation code, this superset elsewhere. The two
// deliberately differ (this one also detects name-scrubbed payloads) -- don't
// unify them without collapsing that difference.
export const isDeletedUser = (user) =>
  Boolean(
    user && (user.is_deleted || user.name === DELETED_NAME || user.first_name === DELETED_NAME),
  );

// Localized label for a soft-deleted account ("Borttagen användare").
export const deletedUserLabel = (intl) =>
  intl.formatMessage({ id: 'DeletedUser', defaultMessage: 'Deleted user' });

// Display name for a user: the deleted-user label for soft-deleted accounts,
// the `name` field when present, or a trimmed `first_name`/`last_name` join.
export const getUserDisplayName = (user, intl) => {
  if (isDeletedUser(user)) return deletedUserLabel(intl);
  if (!user) return '';
  if (user.name) return user.name;
  return combineName(user.first_name, user.last_name);
};
